import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import Layout from './components/layout/Layout'
import CasaApp from './pages/CasaApp'
import Home from './pages/Home'
import Insights from './pages/Insights'
import Kontakt from './pages/Kontakt'
import Leistungen from './pages/Leistungen'
import NotFound from './pages/NotFound'
import Post from './pages/Post'
import UeberUns from './pages/UeberUns'
import Verwalten from './pages/Verwalten'
import { posts } from './articles'

/*
 * The two legal documents are split out of the main bundle. The
 * Datenschutzerklärung alone is ~55 KB of inlined Markdown - a seventh of the
 * whole JavaScript payload - and it is the least-visited page on the site.
 * Everything else stays in the entry chunk: the pages are small, and splitting
 * them would only trade bundle size for a navigation spinner.
 */
const Impressum = lazy(() => import('./pages/Impressum'))
const Datenschutz = lazy(() => import('./pages/Datenschutz'))

/** Holds the dark band the header needs so nothing flashes white while the
    legal chunk loads. */
function PageFallback() {
  return (
    <div
      style={{ minHeight: '60vh', background: 'var(--navy)' }}
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">Seite wird geladen ...</span>
    </div>
  )
}

/**
 * Redirects that keep the old site's URLs working.
 *
 * Two things changed. WordPress had the home page reachable both as `/` and as
 * `/startseite/`, and it spelled the privacy policy `/datenschutzerklaerung/`.
 * Separately, the articles were published at the domain root
 * (`/richtiges-stosslueften-tipps-fuer-ein-gesundes-raumklima/`) rather than
 * under a blog path, and they now live under `/insights/`.
 *
 * Anything still pointing at the old addresses - search results, the Google
 * Business profile, an e-mail signature - would otherwise hit a 404. Only paths
 * that actually existed in the export are redirected; guessing at others would
 * shadow the 404 page for genuinely wrong URLs.
 *
 * These are client-side redirects. Configure the same ones as 301s at the edge
 * (see web/README.md) so crawlers see the permanent move.
 */
const LEGACY_PATHS = {
  '/startseite': '/',
  '/datenschutzerklaerung': '/datenschutz',
}

export default function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="verwalten" element={<Verwalten />} />
        <Route path="leistungen" element={<Leistungen />} />
        <Route path="ueber-uns" element={<UeberUns />} />
        <Route path="casa-app" element={<CasaApp />} />
        <Route path="kontakt" element={<Kontakt />} />
        <Route path="insights" element={<Insights />} />
        <Route path="insights/:slug" element={<Post />} />
        <Route
          path="impressum"
          element={
            <Suspense fallback={<PageFallback />}>
              <Impressum />
            </Suspense>
          }
        />
        <Route
          path="datenschutz"
          element={
            <Suspense fallback={<PageFallback />}>
              <Datenschutz />
            </Suspense>
          }
        />

        {/* Old page URLs. Trailing slashes need no handling - the router
            matches `/verwalten/` and `/verwalten` to the same route. */}
        {Object.entries(LEGACY_PATHS).map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}

        {/* Old article URLs, which sat at the root */}
        {posts.map((post) => (
          <Route
            key={post.slug}
            path={post.slug}
            element={<Navigate to={`/insights/${post.slug}`} replace />}
          />
        ))}

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}
